import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeftRight, Boxes, TrendingUp, Network, Sigma, Blocks, ChevronDown,
} from "lucide-react";

/* ---- Animated per-strategy mini diagrams ---- */
function Node({ x, y, w = 88, h = 34, label, sub }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="7" fill="#0A0B10" stroke="rgba(45,212,191,0.35)" />
      <text x={x + w / 2} y={y + (sub ? 15 : h / 2 + 4)} textAnchor="middle" fill="#fff" fontSize="11" fontFamily="'JetBrains Mono',monospace">{label}</text>
      {sub && <text x={x + w / 2} y={y + 27} textAnchor="middle" fill="#2DD4BF" fontSize="9" fontFamily="'JetBrains Mono',monospace">{sub}</text>}
    </g>
  );
}
function Flow({ d }) {
  return (
    <>
      <path d={d} fill="none" stroke="rgba(45,212,191,0.3)" strokeWidth="1.5" className="qs-flow-line" />
      <circle r="3" fill="#2DD4BF"><animateMotion dur="2.2s" repeatCount="indefinite" path={d} /></circle>
    </>
  );
}

function StrategyDiagram({ slug }) {
  const common = "w-full h-[150px]";
  if (slug === "statistical") {
    return (
      <svg viewBox="0 0 320 150" className={common}>
        <line x1="10" y1="120" x2="310" y2="120" stroke="#1E293B" />
        <path d="M10,90 C90,40 130,40 160,80 C200,120 250,60 310,55" fill="none" stroke="#2DD4BF" strokeWidth="2" />
        <path d="M10,60 C90,110 130,110 160,80 C200,40 250,100 310,95" fill="none" stroke="#00E676" strokeWidth="2" />
        <circle cx="160" cy="80" r="5" fill="#fff"><animate attributeName="r" values="4;7;4" dur="1.6s" repeatCount="indefinite" /></circle>
        <text x="160" y="30" textAnchor="middle" fill="#94A3B8" fontSize="10" fontFamily="'JetBrains Mono',monospace">CONVERGENCE</text>
      </svg>
    );
  }
  if (slug === "mev") {
    return (
      <svg viewBox="0 0 320 150" className={common}>
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <rect x={20 + i * 62} y="55" width="48" height="40" rx="6" fill="#0A0B10" stroke="rgba(45,212,191,0.35)" />
            <text x={44 + i * 62} y="80" textAnchor="middle" fill="#94A3B8" fontSize="9" fontFamily="monospace">blk#{i}</text>
          </g>
        ))}
        <Flow d="M212,75 L250,75" />
        <Node x={250} y={58} w={60} h={34} label="Detect" />
        <text x="120" y="120" textAnchor="middle" fill="#2DD4BF" fontSize="9" fontFamily="'JetBrains Mono',monospace">mempool → ordering → opportunity</text>
      </svg>
    );
  }
  if (slug === "cross-market") {
    return (
      <svg viewBox="0 0 320 150" className={common}>
        {[["CEX", 20, 20], ["DEX", 20, 96], ["FUT", 232, 20], ["CHAIN", 232, 96]].map(([l, x, y]) => (
          <Node key={l} x={x} y={y} w={68} h={30} label={l} />
        ))}
        <Node x={126} y={58} w={68} h={34} label="Q-Core" sub="route" />
        <Flow d="M88,35 L126,72" /><Flow d="M88,111 L126,80" />
        <Flow d="M194,75 L232,35" /><Flow d="M194,80 L232,111" />
      </svg>
    );
  }
  // route diagrams: cex, dex, futures
  const map = {
    cex: [["Exchange A", "buy"], ["Exchange B", "sell"]],
    dex: [["Pool A", "swap"], ["Pool B", "swap"]],
    futures: [["Spot", "long"], ["Perp", "short"]],
  };
  const [a, b] = map[slug] || map.cex;
  return (
    <svg viewBox="0 0 320 150" className={common}>
      <Node x={20} y={58} label={a[0]} sub={a[1]} />
      <Node x={212} y={58} label={b[0]} sub={b[1]} />
      <Flow d="M108,75 L212,75" />
      <text x="160" y="55" textAnchor="middle" fill="#00E676" fontSize="10" fontFamily="'JetBrains Mono',monospace">Δ spread</text>
    </svg>
  );
}

/* ---- Strategy content ---- */
const STRATS = [
  {
    slug: "cex", code: "ARB-01", title: "CEX Arbitrage", icon: ArrowLeftRight,
    summary: "Exploit transient price differences for the same asset across centralized exchanges.",
    flow: ["Detect price gap for an asset across two CEXs", "Assess fees, withdrawal limits and execution latency", "Buy on the cheaper venue, sell on the richer venue", "Capture the net spread after costs"],
    risk: "Latency, transfer times, exchange withdrawal limits and fees can erode or eliminate the spread. Prices can converge before execution completes.",
  },
  {
    slug: "dex", code: "ARB-02", title: "DEX Arbitrage", icon: Boxes,
    summary: "Capture pricing differences between decentralized liquidity pools and protocols.",
    flow: ["Scan liquidity pools across DEXs and chains", "Model price impact and gas for a candidate route", "Swap through pools to realize the pricing gap", "Settle on-chain — verifiable by transaction hash"],
    risk: "Gas costs, slippage, pool depth and MEV competition affect feasibility. Failed or front-run transactions still incur network fees.",
  },
  {
    slug: "futures", code: "ARB-03", title: "Futures Arbitrage", icon: TrendingUp,
    summary: "Trade the relationship between spot and derivatives (basis / funding) markets.",
    flow: ["Compare spot price against perpetual/futures price", "Evaluate funding rate and basis", "Take offsetting spot and futures positions", "Realize the basis as it normalizes"],
    risk: "Funding rates change, positions require margin, and liquidation risk exists. Basis can widen before it converges.",
  },
  {
    slug: "cross-market", code: "ARB-04", title: "Cross-Market Arbitrage", icon: Network,
    summary: "Connect opportunities that span multiple market types simultaneously.",
    flow: ["Aggregate pricing across CEX, DEX, futures and chains", "Identify multi-leg opportunities through Q-Core", "Sequence execution across venues", "Reconcile the combined result"],
    risk: "Multi-leg execution increases operational complexity and the chance that one leg fails, changing the overall economics.",
  },
  {
    slug: "statistical", code: "ARB-05", title: "Statistical Arbitrage", icon: Sigma,
    summary: "Model correlated assets that temporarily diverge and tend to reconverge.",
    flow: ["Identify historically correlated pairs", "Detect a statistically significant divergence", "Take mean-reversion positions on both legs", "Exit as the relationship reconverges"],
    risk: "Correlations can break down; 'temporary' divergence can persist or widen. Statistical relationships are not guarantees.",
  },
  {
    slug: "mev", code: "ARB-06", title: "MEV / On-chain Strategy", icon: Blocks,
    summary: "Identify on-chain opportunities arising from transaction ordering within blocks.",
    flow: ["Observe the mempool and pending transactions", "Detect ordering-related opportunities", "Construct and submit qualifying transactions", "Verify settlement on-chain"],
    risk: "Highly competitive and time-sensitive. Reverts, gas wars and validator ordering mean many attempts fail. Regulatory and ethical considerations apply.",
  },
];

function Module({ s, open, onToggle }) {
  return (
    <div data-testid={`strategy-module-${s.slug}`} className={`rounded-2xl border transition-colors ${open ? "border-[#0E9E90]/40 bg-[#0E9E90]/[0.04]" : "border-white/5 bg-[#0A0B10] hover:border-white/10"}`}>
      <button onClick={onToggle} data-testid={`strategy-toggle-${s.slug}`} className="w-full flex items-center gap-4 p-5 text-left">
        <span className={`grid place-items-center h-12 w-12 rounded-xl border shrink-0 ${open ? "border-[#0E9E90]/50 bg-[#0E9E90]/15 text-[#2DD4BF]" : "border-white/10 bg-white/[0.02] text-slate-400"}`}>
          <s.icon size={22} strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#0E9E90]">{s.code}</p>
          <h3 className="text-lg font-bold text-white">{s.title}</h3>
          <p className="text-sm text-slate-400 mt-0.5 truncate">{s.summary}</p>
        </div>
        <ChevronDown size={20} className={`shrink-0 text-slate-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden"
          >
            <div className="grid md:grid-cols-2 gap-6 px-5 pb-6">
              <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Market Visualization</p>
                <StrategyDiagram slug={s.slug} />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Example Workflow</p>
                <ol className="space-y-2">
                  {s.flow.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <span className="mt-0.5 grid place-items-center h-5 w-5 shrink-0 rounded-full bg-[#0E9E90]/15 text-[#2DD4BF] font-mono text-[10px]">{i + 1}</span>
                      {f}
                    </li>
                  ))}
                </ol>
                <div className="mt-4 rounded-lg border border-amber-400/20 bg-amber-400/[0.05] p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-amber-300 mb-1">Risk Considerations</p>
                  <p className="text-xs text-amber-100/70 leading-relaxed">{s.risk}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function StrategyModules() {
  const [open, setOpen] = useState("cex");
  return (
    <div className="space-y-3" data-testid="strategy-modules">
      {STRATS.map((s) => (
        <Module key={s.slug} s={s} open={open === s.slug} onToggle={() => setOpen(open === s.slug ? null : s.slug)} />
      ))}
    </div>
  );
}
